import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { FiArrowLeft, FiMapPin, FiHome, FiKey } from "react-icons/fi";
import Layout from "../../components/Layout";
import Loading from "../../components/Loading";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import formatValue from "../../utils/formatValue";
import { normalizeKeyLocationLabel } from "../../services/propertyKeyLocation";
import PropertyCommissionSummary from "../../components/PropertyCommissionSummary";

import {
  Container,
  Content,
  ErrorMessageContainer,
  HeaderContainer,
  BackButton,
  TitleBlock,
  PropertyTitle,
  PropertyCode,
  HeaderAside,
  StatusBadge,
  HeroGrid,
  HeroCard,
  HeroCardTitle,
  HeroHighlights,
  HighlightItem,
  MainGrid,
  SectionCard,
  SectionTitle,
  InfoGrid,
  InfoItem,
  PhotosGrid,
  PhotoCard,
  PeopleGrid,
  PersonCard,
  EmptyStateCard,
  FinanceGrid,
  FinanceCard,
} from "./styles";

interface IParams {
  id: string;
}

interface IApiResponse {
  property: {
    id: number;
    title: string;
    status_id: number;
    consultant_id: number;
    rent_value: string;
    condominium_value: string;
    iptu_value: string;
    /** Flattened complement when API omits nested address.floor */
    floor?: string | null;
    kenlo_property_id: string | null;
    status: {
      id: number;
      name: string;
    };
    address: {
      id: number;
      address: string;
      number: string;
      floor?: string | null;
      neighborhood: string;
      city: string;
      uf: string;
    };
    photos: {
      id: number;
      url: string;
      order: number;
    }[];
    consultant: {
      id: number;
      first_name: string;
      email: string;
      phone: string;
    } | null;
    owner: {
      id: number;
      first_name: string;
      email: string;
      phone: string;
    } | null;
    /** API may send string or `{ name: string }` */
    key_location?: unknown;
    key_location_note?: string | null;
  };
  kenlo_contract: {
    contract_id: string | null;
    property_id: string | null;
    code: string | null;
    property: {
      type: string | null;
      description: string | null;
      condominium: string | null;
      internal_code: string | null;
      address: {
        street: string | null;
        number: string | null;
        complement: string | null;
        district: string | null;
        city: string | null;
        state: string | null;
        zip_code: string | null;
      };
    };
    tenants: Array<{
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    }>;
    owners: Array<{
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    }>;
    main_tenant: {
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    } | null;
    main_owner: {
      id: string | null;
      name: string | null;
      email: string | null;
      phone: string | null;
      is_main: boolean;
      fraction: string | null;
    } | null;
    term: {
      start_date: string | null;
      end_date: string | null;
      start_date_iso: string | null;
      end_date_iso: string | null;
      duration_months: number | null;
    };
    rent: {
      amount: string | null;
      administration_fee: string | null;
      due_day: string | null;
    };
    status: {
      code: string | null;
      label: string | null;
    };
    owner_summary: {
      name: string | null;
    };
    raw?: {
      updated_at?: string | null;
      occupation_date?: string | null;
      resignation_date?: string | null;
    };
  } | null;
  kenlo_message?: string | null;
  has_kenlo_contract: boolean;
}

const MyPropertyDetails: React.FC = () => {
  const history = useHistory();
  const { params } = useRouteMatch<IParams>();
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IApiResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    async function loadApi() {
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get(`/api/adm/properties/${params.id}/details`);

        setData(response.data.data ?? response.data);
        setErrorMessage("");
      } catch (err: any) {
        const message =
          err?.response?.data?.message ?? "Erro ao carregar detalhes do imóvel";

        setErrorMessage(message);

        addToast({
          title: message,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      loadApi();
    } else {
      history.push("/");
    }
  }, [access_token, dateToExpires, params.id, user, history, addToast]);

  const property = data?.property;
  const kenloContract = data?.kenlo_contract;

  const fullAddress = useMemo(() => {
    if (!property?.address) return "-";

    const { address, number, neighborhood, city, uf } = property.address;
    const floor =
      property.address.floor ?? property.floor ?? null;

    return [address, number, floor, neighborhood, `${city}/${uf}`]
      .filter((part) => part != null && String(part).trim() !== "")
      .join(", ");
  }, [property]);

  const floorLabel = useMemo(() => {
    const raw = property?.address?.floor ?? property?.floor;
    if (raw == null || String(raw).trim() === "") return null;
    return String(raw).trim();
  }, [property]);

  const keyLocationLabel = useMemo(
    () => normalizeKeyLocationLabel(property?.key_location),
    [property?.key_location]
  );

  const keyLocationNote = useMemo(() => {
    const n = property?.key_location_note;
    if (n == null || String(n).trim() === "") return null;
    return String(n).trim();
  }, [property?.key_location_note]);

  const kenloAddress = useMemo(() => {
    if (!kenloContract?.property?.address) return "-";

    const { street, number, complement, district, city, state, zip_code } =
      kenloContract.property.address;

    return [street, number, complement, district, city, state, zip_code]
      .filter(Boolean)
      .join(", ");
  }, [kenloContract]);

  const totalValue = useMemo(() => {
    const rent = Number(property?.rent_value ?? 0);
    const condominium = Number(property?.condominium_value ?? 0);
    const iptu = Number(property?.iptu_value ?? 0);

    return formatValue(rent + condominium + iptu);
  }, [property]);

  const mainTenant = useMemo(() => {
    if (!kenloContract?.tenants?.length) return null;

    return (
      kenloContract.tenants.find((tenant) => tenant.is_main) ??
      kenloContract.tenants[0]
    );
  }, [kenloContract]);

  function getStatusVariant(
    status?: string
  ): "default" | "success" | "warning" | "danger" {
    const normalized = (status ?? "").toLowerCase();

    if (normalized.includes("alug")) return "success";
    if (normalized.includes("public")) return "default";
    if (normalized.includes("desativ")) return "danger";
    return "warning";
  }

  function getContractStatusVariant(
    status?: string
  ): "default" | "success" | "warning" | "danger" {
    const normalized = (status ?? "").toLowerCase();

    if (normalized.includes("ativo")) return "success";
    if (normalized.includes("paus")) return "warning";
    if (normalized.includes("cancel") || normalized.includes("encerr")) {
      return "danger";
    }

    return "default";
  }

  return (
    <Layout>
      <Container>
        {loading && <Loading />}

        {!loading && (
          <Content>
            {!!errorMessage && (
              <ErrorMessageContainer>
                <h2>{errorMessage}</h2>
              </ErrorMessageContainer>
            )}

            {!errorMessage && property && (
              <>
                <HeaderContainer>
                  <TitleBlock>
                    <BackButton type="button" onClick={() => history.goBack()}>
                      <FiArrowLeft size={18} />
                      Voltar
                    </BackButton>

                    <PropertyTitle>{property.title}</PropertyTitle>
                    <PropertyCode>Imóvel #{property.id}</PropertyCode>
                  </TitleBlock>

                  <HeaderAside>
                    <span>Situação do imóvel</span>
                    <StatusBadge variant={getStatusVariant(property.status?.name)}>
                      {property.status?.name ?? "-"}
                    </StatusBadge>
                  </HeaderAside>
                </HeaderContainer>

                <HeroGrid>
                  <HeroCard>
                    <HeroCardTitle>
                      <FiHome size={18} />
                      Resumo financeiro
                    </HeroCardTitle>

                    <HeroHighlights>
                      <HighlightItem>
                        <strong>Aluguel</strong>
                        <span>{formatValue(Number(property.rent_value ?? 0))}</span>
                      </HighlightItem>

                      <HighlightItem>
                        <strong>Condomínio</strong>
                        <span>
                          {formatValue(Number(property.condominium_value ?? 0))}
                        </span>
                      </HighlightItem>

                      <HighlightItem>
                        <strong>IPTU</strong>
                        <span>{formatValue(Number(property.iptu_value ?? 0))}</span>
                      </HighlightItem>

                      <HighlightItem>
                        <strong>Total mensal</strong>
                        <span>{totalValue}</span>
                      </HighlightItem>
                    </HeroHighlights>
                  </HeroCard>

                  <HeroCard>
                    <HeroCardTitle>
                      <FiMapPin size={18} />
                      Localização
                    </HeroCardTitle>

                    <InfoGrid>
                      <InfoItem>
                        <strong>Endereço completo</strong>
                        <span>{fullAddress}</span>
                      </InfoItem>

                      {floorLabel ? (
                        <InfoItem>
                          <strong>Apartamento / andar / complemento</strong>
                          <span>{floorLabel}</span>
                        </InfoItem>
                      ) : null}

                      <InfoItem>
                        <strong>Bairro</strong>
                        <span>{property.address?.neighborhood ?? "-"}</span>
                      </InfoItem>

                      <InfoItem>
                        <strong>Cidade</strong>
                        <span>{property.address?.city ?? "-"}</span>
                      </InfoItem>

                      <InfoItem>
                        <strong>UF</strong>
                        <span>{property.address?.uf ?? "-"}</span>
                      </InfoItem>
                    </InfoGrid>
                  </HeroCard>
                </HeroGrid>

                <SectionCard>
                  <SectionTitle
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <FiKey size={18} aria-hidden />
                    Local das chaves
                  </SectionTitle>
                  <p
                    style={{
                      color: "#2d2d2d",
                      fontSize: 15,
                      lineHeight: 1.6,
                      wordBreak: "break-word",
                    }}
                  >
                    {keyLocationLabel !== "" ? keyLocationLabel : "—"}
                  </p>
                  {keyLocationNote ? (
                    <InfoGrid style={{ marginTop: 16 }}>
                      <InfoItem>
                        <strong>Observação</strong>
                        <span>{keyLocationNote}</span>
                      </InfoItem>
                    </InfoGrid>
                  ) : null}
                </SectionCard>

                <div className="mb-5">
                  <PropertyCommissionSummary
                    property={property as unknown as Record<string, unknown>}
                  />
                </div>

                <MainGrid>
                  <SectionCard>
                    <SectionTitle>Galeria de fotos</SectionTitle>

                    {property.photos?.length ? (
                      <PhotosGrid>
                        {[...property.photos]
                          .sort((a, b) => a.order - b.order)
                          .map((photo) => (
                            <PhotoCard key={photo.id}>
                              <img
                                src={photo.url}
                                alt={`Foto ${photo.order} do imóvel`}
                              />
                            </PhotoCard>
                          ))}
                      </PhotosGrid>
                    ) : (
                      <EmptyStateCard>
                        <p>Nenhuma foto cadastrada para este imóvel.</p>
                      </EmptyStateCard>
                    )}
                  </SectionCard>

                  <SectionCard>
                    <SectionTitle>Proprietário e inquilino</SectionTitle>

                    <PeopleGrid>
                      <PersonCard>
                        <h3>Proprietário</h3>

                        <InfoItem>
                          <strong>Nome</strong>
                          <span>{property.owner?.first_name ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Email</strong>
                          <span>{property.owner?.email ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Telefone</strong>
                          <span>{property.owner?.phone ?? "-"}</span>
                        </InfoItem>
                      </PersonCard>

                      <PersonCard>
                        <h3>Inquilino</h3>

                        <InfoItem>
                          <strong>Nome</strong>
                          <span>{mainTenant?.name ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Email</strong>
                          <span>{mainTenant?.email ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Telefone</strong>
                          <span>{mainTenant?.phone ?? "-"}</span>
                        </InfoItem>
                      </PersonCard>
                    </PeopleGrid>
                  </SectionCard>

                  <SectionCard>
                    <SectionTitle>Contrato e financeiro Kenlo</SectionTitle>

                    {data?.has_kenlo_contract && kenloContract ? (
                      <FinanceGrid>
                        <FinanceCard>
                          <h3>Contrato</h3>

                          <InfoItem>
                            <strong>Código do contrato</strong>
                            <span>{kenloContract.code ?? "-"}</span>
                          </InfoItem>

                          <InfoItem>
                            <strong>Status</strong>
                            <span>{kenloContract.status?.label ?? "-"}</span>
                          </InfoItem>

                          <InfoItem>
                            <strong>Início</strong>
                            <span>{kenloContract.term?.start_date ?? "-"}</span>
                          </InfoItem>

                          <InfoItem>
                            <strong>Fim</strong>
                            <span>{kenloContract.term?.end_date ?? "-"}</span>
                          </InfoItem>

                          <InfoItem>
                            <strong>Duração</strong>
                            <span>
                              {kenloContract.term?.duration_months != null
                                ? `${kenloContract.term.duration_months} meses`
                                : "-"}
                            </span>
                          </InfoItem>
                        </FinanceCard>

                        <FinanceCard>
                          <h3>Financeiro</h3>

                          <InfoItem>
                            <strong>Aluguel</strong>
                            <span>
                              {kenloContract.rent?.amount
                                ? formatValue(Number(kenloContract.rent.amount))
                                : "-"}
                            </span>
                          </InfoItem>

                          <InfoItem>
                            <strong>Taxa administrativa</strong>
                            <span>
                              {kenloContract.rent?.administration_fee
                                ? `${kenloContract.rent.administration_fee}%`
                                : "-"}
                            </span>
                          </InfoItem>

                          <InfoItem>
                            <strong>Dia do vencimento</strong>
                            <span>{kenloContract.rent?.due_day ?? "-"}</span>
                          </InfoItem>
                        </FinanceCard>
                      </FinanceGrid>
                    ) : (
                      <EmptyStateCard>
                        <p>
                          {data?.kenlo_message ??
                            "Contrato ainda não sincronizado ou inexistente na plataforma financeira."}
                        </p>
                      </EmptyStateCard>
                    )}
                  </SectionCard>

                  {data?.has_kenlo_contract && kenloContract && (
                    <SectionCard>
                      <SectionTitle>Dados do imóvel no Kenlo</SectionTitle>

                      <InfoGrid>
                        <InfoItem>
                          <strong>Tipo</strong>
                          <span>{kenloContract.property?.type ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Descrição</strong>
                          <span>{kenloContract.property?.description ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Condomínio</strong>
                          <span>{kenloContract.property?.condominium ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Código interno</strong>
                          <span>{kenloContract.property?.internal_code ?? "-"}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>Endereço no Kenlo</strong>
                          <span>{kenloAddress}</span>
                        </InfoItem>

                        <InfoItem>
                          <strong>ID Kenlo do imóvel</strong>
                          <span>{property.kenlo_property_id ?? "-"}</span>
                        </InfoItem>
                      </InfoGrid>
                    </SectionCard>
                  )}
                </MainGrid>
              </>
            )}
          </Content>
        )}
      </Container>
    </Layout>
  );
};

export default MyPropertyDetails;